import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  PieChart, Wallet, FileText, Target, TrendingUp, Users, Shield,
  Plus, Upload, DollarSign, BarChart3, CheckCircle, Clock, AlertTriangle,
  Briefcase, Calendar, ArrowRight, Lightbulb, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StartupDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [startupId, setStartupId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [capTable, setCapTable] = useState({ entries: [], summary: {} });
  const [fundraising, setFundraising] = useState({ rounds: [] });
  const [documents, setDocuments] = useState([]);
  const [milestones, setMilestones] = useState({ milestones: [], summary: {} });
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showCapTableModal, setShowCapTableModal] = useState(false);
  const [showFundraisingModal, setShowFundraisingModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showWaterfallModal, setShowWaterfallModal] = useState(false);
  const [waterfallInputs, setWaterfallInputs] = useState({ exit_value: 10000000, expenses: 0, liquidation_preference_multiple: 1.0, investor_preferred: true });
  const [waterfallResult, setWaterfallResult] = useState(null);
  const [waterfallLoading, setWaterfallLoading] = useState(false);
  const startupInfo = { name: capTable?.summary?.startup_name || 'Startup' };

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    // Get startup ID from user profile or first startup
    const fetchStartup = async () => {
      try {
        const res = await axios.get(`${API}/startups`, { headers });
        if (res.data.length > 0) {
          setStartupId(res.data[0].id);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchStartup();
  }, []);

  useEffect(() => {
    if (startupId) {
      fetchAllData();
    }
  }, [startupId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, capRes, fundRes, docsRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/analytics/startup/${startupId}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/cap-table/${startupId}`, { headers }).catch(() => ({ data: { entries: [], summary: {} } })),
        axios.get(`${API}/fundraising/${startupId}`, { headers }).catch(() => ({ data: { rounds: [] } })),
        axios.get(`${API}/documents/${startupId}`, { headers }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/milestones/${startupId}`, { headers }).catch(() => ({ data: { milestones: [], summary: {} } }))
      ]);

      setAnalytics(analyticsRes.data);
      setCapTable(capRes.data);
      setFundraising(fundRes.data);
      setDocuments(docsRes.data.documents || []);
      setMilestones(milestonesRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case "warning": return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case "success": return <CheckCircle className="h-5 w-5 text-green-500" />;
      default: return <Lightbulb className="h-5 w-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50">
      <Header />

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Building2 className="h-8 w-8" />
                Startup Command Center
              </h1>
              <p className="opacity-90 mt-1">Manage fundraising, cap table, documents & analytics</p>
            </div>
            <Button onClick={() => navigate('/pricing')} className="bg-white text-green-600 hover:bg-gray-100">
              <TrendingUp className="h-4 w-4 mr-2" /> Upgrade Plan
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 w-full max-w-3xl mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="fundraising">Fundraising</TabsTrigger>
            <TabsTrigger value="captable">Cap Table</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            {/* Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Governance Score</p>
                      <p className="text-4xl font-bold">{analytics?.metrics?.governance_score || 0}/10</p>
                    </div>
                    <Shield className="h-12 w-12 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Total Raised</p>
                      <p className="text-4xl font-bold">${(analytics?.metrics?.total_raised || 0).toLocaleString()}</p>
                    </div>
                    <DollarSign className="h-12 w-12 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Shareholders</p>
                      <p className="text-4xl font-bold">{analytics?.metrics?.shareholder_count || 0}</p>
                    </div>
                    <Users className="h-12 w-12 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Documents</p>
                      <p className="text-4xl font-bold">{analytics?.metrics?.documents_count || 0}</p>
                    </div>
                    <FileText className="h-12 w-12 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Insights Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-500" />
                  AI-Powered Insights & Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.insights?.length > 0 ? (
                  <div className="space-y-4">
                    {analytics.insights.map((insight, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                        insight.type === 'warning' ? 'bg-amber-50 border-amber-500' :
                        insight.type === 'success' ? 'bg-green-50 border-green-500' :
                        'bg-blue-50 border-blue-500'
                      }`}>
                        <div className="flex items-start gap-3">
                          {getInsightIcon(insight.type)}
                          <div className="flex-1">
                            <h4 className="font-semibold">{insight.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
                            <Button variant="link" className="p-0 h-auto mt-2 text-sm">
                              {insight.action} <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No insights yet. Complete your profile to get personalized recommendations.</p>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Button onClick={() => setActiveTab('fundraising')} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                <Wallet className="h-6 w-6 text-green-600" />
                <span>Start Fundraising</span>
              </Button>
              <Button onClick={() => setActiveTab('captable')} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                <PieChart className="h-6 w-6 text-blue-600" />
                <span>Manage Cap Table</span>
              </Button>
              <Button onClick={() => setActiveTab('documents')} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                <Upload className="h-6 w-6 text-purple-600" />
                <span>Upload Documents</span>
              </Button>
              <Button onClick={() => navigate('/matching/investors')} variant="outline" className="h-auto p-4 flex flex-col gap-2">
                <Users className="h-6 w-6 text-amber-600" />
                <span>Find Investors</span>
              </Button>
            </div>
          </TabsContent>

          {/* FUNDRAISING TAB */}
          <TabsContent value="fundraising">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Fundraising Rounds</h2>
              <Button onClick={() => setShowFundraisingModal(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" /> New Round
              </Button>
            </div>

            {fundraising.rounds?.length > 0 ? (
              <div className="space-y-6">
                {fundraising.rounds.map((round) => (
                  <Card key={round.id} className="overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {round.round_name}
                            <Badge variant={round.status === 'active' ? 'default' : 'secondary'}>
                              {round.status}
                            </Badge>
                          </CardTitle>
                          <CardDescription>
                            Target: ${round.target_amount?.toLocaleString()} {round.currency}
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-green-600">
                            {round.progress_percentage?.toFixed(0) || 0}%
                          </p>
                          <p className="text-sm text-gray-500">
                            ${round.raised_amount?.toLocaleString() || 0} raised
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <Progress value={round.progress_percentage || 0} className="h-3 mb-4" />
                      
                      <div className="grid grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-500">Instrument</p>
                          <p className="font-medium">{round.instrument_type || 'Equity'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Pre-Money</p>
                          <p className="font-medium">${(round.valuation_pre || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Min Investment</p>
                          <p className="font-medium">${(round.min_investment || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Commitments</p>
                          <p className="font-medium">{round.commitments?.length || 0}</p>
                        </div>
                      </div>

                      {round.commitments?.length > 0 && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold mb-3">Investor Commitments</h4>
                          <div className="space-y-2">
                            {round.commitments.map((c) => (
                              <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                  <p className="font-medium">{c.investor_name}</p>
                                  <p className="text-sm text-gray-500">{c.commitment_date}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold">${c.amount?.toLocaleString()}</p>
                                  <Badge variant={
                                    c.status === 'funded' ? 'default' :
                                    c.status === 'signed' ? 'secondary' : 'outline'
                                  }>{c.status}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Wallet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Active Fundraising Rounds</h3>
                <p className="text-gray-500 mb-6">Start your first fundraising round to track investor commitments</p>
                <Button onClick={() => setShowFundraisingModal(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" /> Create First Round
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* CAP TABLE TAB */}
          <TabsContent value="captable">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
              <h2 className="text-2xl font-bold">Cap Table Management</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={async () => {
                    try {
                      const startupName = (startupInfo?.name || 'Startup').replace(/[^A-Za-z0-9 _-]/g, '').trim();
                      const res = await axios.get(`${API}/cap-table/${startupId}/pdf`, { headers, params: { startup_name: startupName }, responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `SKIAxis_CapTable_${startupName.replace(/\s+/g, '_')}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      alert('No cap table entries yet — add shareholders first.');
                    }
                  }}
                  variant="outline"
                  className="border-[#C8A84B] text-[#0D1B2A]"
                  data-testid="cap-export-pdf-btn"
                >
                  Export PDF
                </Button>
                <Button
                  onClick={() => setShowWaterfallModal(true)}
                  variant="outline"
                  className="border-[#0D1B2A] text-[#0D1B2A]"
                  data-testid="cap-waterfall-btn"
                >
                  Exit Waterfall
                </Button>
                <Button onClick={() => setShowCapTableModal(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" /> Add Shareholder
                </Button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Total Shares</p>
                  <p className="text-2xl font-bold">{capTable.summary?.total_shares?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Shareholders</p>
                  <p className="text-2xl font-bold">{capTable.summary?.shareholder_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Founder Ownership</p>
                  <p className="text-2xl font-bold text-green-600">{capTable.summary?.founder_ownership?.toFixed(1) || 0}%</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Investor Ownership</p>
                  <p className="text-2xl font-bold text-blue-600">{capTable.summary?.investor_ownership?.toFixed(1) || 0}%</p>
                </CardContent>
              </Card>
            </div>

            {capTable.entries?.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-4">Shareholder</th>
                        <th className="text-left p-4">Type</th>
                        <th className="text-right p-4">Shares</th>
                        <th className="text-right p-4">Ownership</th>
                        <th className="text-right p-4">Investment</th>
                        <th className="text-center p-4">Vested</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capTable.entries.map((entry) => (
                        <tr key={entry.id} className="border-t hover:bg-gray-50">
                          <td className="p-4 font-medium">{entry.shareholder_name}</td>
                          <td className="p-4">
                            <Badge variant={
                              entry.shareholder_type === 'founder' ? 'default' :
                              entry.shareholder_type === 'investor' ? 'secondary' : 'outline'
                            }>{entry.shareholder_type}</Badge>
                          </td>
                          <td className="p-4 text-right">{entry.shares?.toLocaleString()}</td>
                          <td className="p-4 text-right font-medium">{entry.ownership_percentage?.toFixed(2)}%</td>
                          <td className="p-4 text-right">${entry.investment_amount?.toLocaleString() || '-'}</td>
                          <td className="p-4 text-center">
                            <Progress value={entry.vested_percentage || 100} className="h-2 w-20 mx-auto" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            ) : (
              <Card className="p-12 text-center">
                <PieChart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Cap Table Empty</h3>
                <p className="text-gray-500 mb-6">Add shareholders to track equity distribution</p>
                <Button onClick={() => setShowCapTableModal(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" /> Add First Shareholder
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Document Vault</h2>
              <Button onClick={() => setShowDocumentModal(true)} className="bg-green-600 hover:bg-green-700">
                <Upload className="h-4 w-4 mr-2" /> Upload Document
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.length > 0 ? documents.map((doc) => (
                <Card key={doc.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-3 rounded-lg ${
                        doc.document_type === 'pitch_deck' ? 'bg-purple-100' :
                        doc.document_type === 'financials' ? 'bg-green-100' :
                        doc.document_type === 'legal' ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        <FileText className={`h-6 w-6 ${
                          doc.document_type === 'pitch_deck' ? 'text-purple-600' :
                          doc.document_type === 'financials' ? 'text-green-600' :
                          doc.document_type === 'legal' ? 'text-red-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold">{doc.title}</h4>
                        <p className="text-sm text-gray-500">{doc.document_type}</p>
                        <p className="text-xs text-gray-400 mt-1">{doc.file_name}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" variant="outline" className="flex-1">View</Button>
                      <Button size="sm" variant="outline">Share</Button>
                    </div>
                  </CardContent>
                </Card>
              )) : (
                <Card className="col-span-full p-12 text-center">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Documents Yet</h3>
                  <p className="text-gray-500 mb-6">Upload pitch decks, financials, and legal docs for investors</p>
                  <Button onClick={() => setShowDocumentModal(true)} className="bg-green-600 hover:bg-green-700">
                    <Upload className="h-4 w-4 mr-2" /> Upload First Document
                  </Button>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* MILESTONES TAB */}
          <TabsContent value="milestones">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">Milestone Tracking</h2>
                <p className="text-gray-500">Link milestones to funding tranches</p>
              </div>
              <Button onClick={() => setShowMilestoneModal(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" /> Add Milestone
              </Button>
            </div>

            {/* Summary */}
            {milestones.summary && (
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold">{milestones.summary.total || 0}</p>
                    <p className="text-sm text-gray-500">Total</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">{milestones.summary.completed || 0}</p>
                    <p className="text-sm text-gray-500">Completed</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-blue-600">{milestones.summary.in_progress || 0}</p>
                    <p className="text-sm text-gray-500">In Progress</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">{milestones.summary.delayed || 0}</p>
                    <p className="text-sm text-gray-500">Delayed</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {milestones.milestones?.length > 0 ? (
              <div className="space-y-4">
                {milestones.milestones.map((m) => (
                  <Card key={m.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-full ${
                            m.status === 'completed' ? 'bg-green-100' :
                            m.status === 'in_progress' ? 'bg-blue-100' :
                            m.status === 'delayed' ? 'bg-amber-100' : 'bg-gray-100'
                          }`}>
                            {m.status === 'completed' ? <CheckCircle className="h-5 w-5 text-green-600" /> :
                             m.status === 'delayed' ? <AlertTriangle className="h-5 w-5 text-amber-600" /> :
                             <Clock className="h-5 w-5 text-gray-500" />}
                          </div>
                          <div>
                            <h4 className="font-semibold">{m.title}</h4>
                            <p className="text-sm text-gray-500">{m.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge>{m.category}</Badge>
                          <p className="text-sm text-gray-500 mt-1">Due: {m.target_date}</p>
                          {m.linked_funding && (
                            <p className="text-sm font-medium text-green-600">${m.linked_funding.toLocaleString()} linked</p>
                          )}
                        </div>
                      </div>
                      <Progress value={m.progress_percentage || 0} className="h-2 mt-4" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Milestones Set</h3>
                <p className="text-gray-500 mb-6">Set milestones to track progress and unlock funding tranches</p>
                <Button onClick={() => setShowMilestoneModal(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" /> Create First Milestone
                </Button>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* WATERFALL SIMULATOR MODAL */}
      {showWaterfallModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowWaterfallModal(false)} data-testid="waterfall-modal">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b-2 border-[#C8A84B] bg-[#0D1B2A] text-[#FAF7F0]">
              <div className="text-[11px] font-bold tracking-[.3em] uppercase text-[#C8A84B] mb-2">Exit Waterfall Simulator</div>
              <div className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Display',serif" }}>Who gets paid what <em className="text-[#C8A84B] italic">at exit</em>.</div>
              <p className="text-sm text-white/70 mt-1">1× non-participating preferred model · pro-rata waterfall for common shareholders.</p>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-[11px] tracking-[.2em] uppercase font-bold text-gray-600">Exit Value (AED)</label>
                <input type="number" className="w-full border px-3 py-2 mt-1" value={waterfallInputs.exit_value}
                  onChange={(e) => setWaterfallInputs((p) => ({ ...p, exit_value: parseFloat(e.target.value) || 0 }))}
                  data-testid="waterfall-exit-value" />
              </div>
              <div>
                <label className="text-[11px] tracking-[.2em] uppercase font-bold text-gray-600">Transaction Expenses (AED)</label>
                <input type="number" className="w-full border px-3 py-2 mt-1" value={waterfallInputs.expenses}
                  onChange={(e) => setWaterfallInputs((p) => ({ ...p, expenses: parseFloat(e.target.value) || 0 }))}
                  data-testid="waterfall-expenses" />
              </div>
              <div>
                <label className="text-[11px] tracking-[.2em] uppercase font-bold text-gray-600">LP Multiple</label>
                <input type="number" step="0.25" className="w-full border px-3 py-2 mt-1" value={waterfallInputs.liquidation_preference_multiple}
                  onChange={(e) => setWaterfallInputs((p) => ({ ...p, liquidation_preference_multiple: parseFloat(e.target.value) || 1 }))}
                  data-testid="waterfall-lp-mult" />
              </div>
            </div>
            <div className="px-6 pb-4">
              <Button
                className="bg-[#C8A84B] hover:bg-[#0D1B2A] hover:text-[#C8A84B] text-[#0D1B2A] font-bold tracking-[.18em] uppercase text-xs"
                disabled={waterfallLoading}
                data-testid="waterfall-simulate-btn"
                onClick={async () => {
                  setWaterfallLoading(true);
                  try {
                    const res = await axios.post(`${API}/cap-table/${startupId}/waterfall`, waterfallInputs, { headers });
                    setWaterfallResult(res.data);
                  } catch (e) {
                    alert(e?.response?.data?.detail || 'No cap table entries — add shareholders first.');
                  } finally {
                    setWaterfallLoading(false);
                  }
                }}
              >
                {waterfallLoading ? 'Simulating…' : 'Simulate exit →'}
              </Button>
            </div>
            {waterfallResult && (
              <div className="px-6 pb-6" data-testid="waterfall-result">
                <div className="bg-[#FAF7F0] border border-[#E8DFC9] p-4 grid grid-cols-3 gap-4 mb-4">
                  <div><div className="text-[10px] tracking-[.22em] uppercase font-bold text-[#C8A84B]">Distributable</div><div className="font-bold text-[#0D1B2A]" style={{ fontFamily: "'Cormorant Display',serif", fontSize: 22 }}>AED {Number(waterfallResult.distributable || 0).toLocaleString()}</div></div>
                  <div><div className="text-[10px] tracking-[.22em] uppercase font-bold text-[#C8A84B]">Total Paid</div><div className="font-bold text-[#0D1B2A]" style={{ fontFamily: "'Cormorant Display',serif", fontSize: 22 }}>AED {Number(waterfallResult.total_paid || 0).toLocaleString()}</div></div>
                  <div><div className="text-[10px] tracking-[.22em] uppercase font-bold text-[#C8A84B]">Unallocated</div><div className="font-bold text-[#0D1B2A]" style={{ fontFamily: "'Cormorant Display',serif", fontSize: 22 }}>AED {Number(waterfallResult.unallocated || 0).toLocaleString()}</div></div>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="bg-[#0D1B2A] text-[#C8A84B] text-[10px] tracking-[.22em] uppercase"><th className="p-2 text-left">Shareholder</th><th className="p-2 text-left">Type</th><th className="p-2 text-right">Payout (AED)</th><th className="p-2 text-right">Multiple</th><th className="p-2 text-left">Basis</th></tr></thead>
                  <tbody>
                    {waterfallResult.payouts?.map((p, i) => (
                      <tr key={i} className="border-b border-[#E8DFC9]" data-testid={`waterfall-row-${i}`}>
                        <td className="p-2 font-bold text-[#0D1B2A]">{p.shareholder_name}</td>
                        <td className="p-2 text-gray-600 capitalize">{p.shareholder_type}</td>
                        <td className="p-2 text-right font-bold text-[#C8A84B]" style={{ fontFamily: "'Cormorant Display',serif", fontSize: 17 }}>{Number(p.payout).toLocaleString()}</td>
                        <td className="p-2 text-right text-gray-600">{p.multiple_on_invested ? `${p.multiple_on_invested}×` : '—'}</td>
                        <td className="p-2 text-xs text-gray-500 italic">{p.basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StartupDashboard;
